import { useGetAgreements } from "@src/appV2/Agreements/api/useGetAgreements";
import { AgreementKind, type AgreementsResponse } from "@src/appV2/Agreements/types";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { type Worker } from "@src/appV2/Worker/api/types";
import { type QueryObserverResult } from "@tanstack/react-query";
import { minutesToMilliseconds } from "date-fns";

interface UseJobSearchAgreementParams {
  worker: Worker | undefined;
  enabled?: boolean;
}

interface UseJobSearchAgreementResult {
  hasSignedJobSearchAgreement: boolean;
  isJobSearchAgreementRequired: boolean;
  isLoading: boolean;
  refetchJobSearchAgreements: QueryObserverResult<AgreementsResponse>["refetch"];
}

/**
 * Hook to check the Job Search Agreement status for a worker
 *
 * Uses the agreements API with needsSignature=true to determine if the worker
 * needs to sign the job search agreement. If the API returns any agreements,
 * it means the worker hasn't signed and needs to do so.
 *
 * The agreement requirement is shown via banners on the job list and job details
 * pages, allowing users to browse jobs while being prompted to sign the agreement.
 */
export function useJobSearchAgreement(
  params: UseJobSearchAgreementParams
): UseJobSearchAgreementResult {
  const { worker, enabled = true } = params;

  const isJobSearchAgreementEnabled = useCbhFlag(CbhFeatureFlag.JOB_SEARCH_AGREEMENT_ENABLED, {
    defaultValue: false,
  });

  // Query the agreements API to check if the worker needs to sign
  // Cache for 5 minutes since agreement status rarely changes
  const {
    data: agreementsData,
    isLoading: isAgreementsLoading,
    isSuccess: isAgreementsSuccess,
    refetch: refetchJobSearchAgreements,
  } = useGetAgreements(
    {
      workerId: worker?.userId,
      kind: [AgreementKind.JOB_SEARCH],
      needsSignature: true,
    },
    {
      enabled: Boolean(worker?.userId) && isJobSearchAgreementEnabled && enabled,
      staleTime: minutesToMilliseconds(5),
    }
  );

  // If the API returns any agreements with needsSignature=true, the worker hasn't signed
  const needsToSign = isAgreementsSuccess && (agreementsData?.data?.length ?? 0) > 0;
  const hasSignedJobSearchAgreement = isAgreementsSuccess && !needsToSign;

  const isJobSearchAgreementRequired = isJobSearchAgreementEnabled && needsToSign;

  // Loading if worker is undefined or agreements query is loading
  const isLoading = worker === undefined || (isJobSearchAgreementEnabled && isAgreementsLoading);

  return {
    hasSignedJobSearchAgreement,
    isJobSearchAgreementRequired,
    isLoading,
    refetchJobSearchAgreements,
  };
}
